import { useEffect, useRef } from 'react';

export const DEV_LOADING_TRACE_STORAGE_KEY = 'dt-debug-loading';

const DEFAULT_SLOW_THRESHOLDS_MS = [1000, 3000, 5000, 10000] as const;
const DEFAULT_REPEAT_SLOW_MS = 10000;
const HISTORY_LIMIT = 100;

export type DevLoadingTraceDetails = Record<string, unknown>;

export interface DevLoadingTraceOptions {
  slowThresholdsMs?: readonly number[];
  repeatSlowMs?: number;
}

type DevLoadingTraceDetailsSource =
  | DevLoadingTraceDetails
  | (() => DevLoadingTraceDetails);

type LoadingTraceEventType = 'start' | 'slow' | 'end';

interface LoadingTraceMetadata {
  route: string;
  elapsedMs: number;
  visibilityState: DocumentVisibilityState | 'unknown';
  hasFocus: boolean | 'unknown';
  details: DevLoadingTraceDetails;
  changedLoadingFlags: Record<string, { from: boolean; to: boolean }>;
  startedAt: string;
}

interface ActiveSpanSnapshot extends LoadingTraceMetadata {
  id: string;
  key: string;
  lastEvent: LoadingTraceEventType;
}

interface LoadingTraceHistoryEvent extends LoadingTraceMetadata {
  id: string;
  key: string;
  event: LoadingTraceEventType;
  at: string;
}

interface LoadingTraceRegistry {
  active: Record<string, ActiveSpanSnapshot>;
  history: LoadingTraceHistoryEvent[];
}

interface SpanState {
  id: string;
  key: string;
  startedAtMs: number;
  startedAt: string;
  initialFlags: Record<string, boolean>;
  timeoutId: number | null;
  slowThresholdsMs: number[];
  repeatSlowMs: number;
  nextSlowIndex: number;
  nextRepeatedSlowMs: number;
  lastEvent: LoadingTraceEventType;
}

declare global {
  interface Window {
    __DT_LOADING_TRACE__?: LoadingTraceRegistry;
  }
}

let nextSpanId = 1;

export function isDevLoadingTraceEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(DEV_LOADING_TRACE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function toDevLoadingTraceKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function useDevLoadingTrace(
  key: string,
  active: boolean,
  details: DevLoadingTraceDetails = {},
  options: DevLoadingTraceOptions = {}
): void {
  const spanRef = useRef<SpanState | null>(null);
  const detailsRef = useRef<DevLoadingTraceDetails>({});
  const repeatSlowMs = options.repeatSlowMs;
  const slowThresholdsMs = options.slowThresholdsMs;
  detailsRef.current = details;

  useEffect(() => {
    const span = spanRef.current;
    if (!span) return;

    writeActiveSnapshot(span, detailsRef.current);
  }, [details]);

  useEffect(() => {
    if (!active || !isDevLoadingTraceEnabled()) {
      return undefined;
    }

    const span = startSpan(key, () => detailsRef.current, {
      repeatSlowMs,
      slowThresholdsMs,
    });
    spanRef.current = span;

    return () => {
      endSpan(span, detailsRef.current);
      if (spanRef.current?.id === span.id) {
        spanRef.current = null;
      }
    };
  }, [active, key, repeatSlowMs, slowThresholdsMs]);
}

export async function runWithDevLoadingTrace<T>(
  key: string,
  work: () => T | Promise<T>,
  details: DevLoadingTraceDetailsSource = {},
  options: DevLoadingTraceOptions = {}
): Promise<T> {
  if (!isDevLoadingTraceEnabled()) {
    return await work();
  }

  const getDetails = () => readTraceDetails(details);
  const span = startSpan(key, getDetails, options);

  try {
    const result = await work();
    endSpan(span, { ...getDetails(), outcome: 'success' });
    return result;
  } catch (error) {
    endSpan(span, {
      ...getDetails(),
      outcome: 'error',
      ...errorTraceDetails(error),
    });
    throw error;
  }
}

function startSpan(
  key: string,
  getDetails: () => DevLoadingTraceDetails,
  options: DevLoadingTraceOptions
): SpanState {
  const details = getDetails();
  const slowThresholdsMs = normalizeThresholds(
    options.slowThresholdsMs ?? DEFAULT_SLOW_THRESHOLDS_MS
  );
  const repeatSlowMs = Math.max(
    1,
    options.repeatSlowMs ?? DEFAULT_REPEAT_SLOW_MS
  );
  const lastThreshold =
    slowThresholdsMs[slowThresholdsMs.length - 1] ?? repeatSlowMs;
  const span: SpanState = {
    id: `${key}#${nextSpanId++}`,
    key,
    startedAtMs: Date.now(),
    startedAt: new Date().toISOString(),
    initialFlags: booleanFlags(details),
    timeoutId: null,
    slowThresholdsMs,
    repeatSlowMs,
    nextSlowIndex: 0,
    nextRepeatedSlowMs: lastThreshold + repeatSlowMs,
    lastEvent: 'start',
  };

  logSpan('start', span, details);
  scheduleNextSlowLog(span, getDetails);
  return span;
}

function endSpan(span: SpanState, details: DevLoadingTraceDetails): void {
  if (span.timeoutId !== null) {
    window.clearTimeout(span.timeoutId);
    span.timeoutId = null;
  }

  logSpan('end', span, details);
  const registry = getRegistry();
  delete registry.active[span.id];
}

function scheduleNextSlowLog(
  span: SpanState,
  getDetails: () => DevLoadingTraceDetails
): void {
  const nextThreshold =
    span.slowThresholdsMs[span.nextSlowIndex] ?? span.nextRepeatedSlowMs;

  if (span.nextSlowIndex < span.slowThresholdsMs.length) {
    span.nextSlowIndex += 1;
  } else {
    span.nextRepeatedSlowMs += span.repeatSlowMs;
  }

  const delayMs = Math.max(0, nextThreshold - elapsedMs(span));
  span.timeoutId = window.setTimeout(() => {
    logSpan('slow', span, getDetails(), nextThreshold);
    scheduleNextSlowLog(span, getDetails);
  }, delayMs);
}

function logSpan(
  event: LoadingTraceEventType,
  span: SpanState,
  details: DevLoadingTraceDetails,
  thresholdMs?: number
): void {
  const metadata = buildMetadata(span, details);
  const registry = getRegistry();
  const at = new Date().toISOString();

  registry.history.push({
    ...metadata,
    id: span.id,
    key: span.key,
    event,
    at,
  });
  if (registry.history.length > HISTORY_LIMIT) {
    registry.history.splice(0, registry.history.length - HISTORY_LIMIT);
  }
  span.lastEvent = event;

  if (event === 'end') {
    delete registry.active[span.id];
  } else {
    registry.active[span.id] = {
      ...metadata,
      id: span.id,
      key: span.key,
      lastEvent: event,
    };
  }

  if (event === 'slow') {
    // eslint-disable-next-line no-console -- Dev-only trace output is the feature.
    console.info(`[dt-loading] slow ${thresholdMs}ms ${span.key}`, metadata);
  } else if (event === 'end') {
    // eslint-disable-next-line no-console -- Dev-only trace output is the feature.
    console.info(
      `[dt-loading] end ${metadata.elapsedMs}ms ${span.key}`,
      metadata
    );
  } else {
    // eslint-disable-next-line no-console -- Dev-only trace output is the feature.
    console.info(`[dt-loading] start ${span.key}`, metadata);
  }
}

function writeActiveSnapshot(
  span: SpanState,
  details: DevLoadingTraceDetails
): void {
  if (!isDevLoadingTraceEnabled()) return;
  const metadata = buildMetadata(span, details);
  getRegistry().active[span.id] = {
    ...metadata,
    id: span.id,
    key: span.key,
    lastEvent: span.lastEvent,
  };
}

function buildMetadata(
  span: SpanState,
  details: DevLoadingTraceDetails
): LoadingTraceMetadata {
  return {
    route: currentRoute(),
    elapsedMs: elapsedMs(span),
    visibilityState:
      typeof document === 'undefined' ? 'unknown' : document.visibilityState,
    hasFocus: typeof document === 'undefined' ? 'unknown' : document.hasFocus(),
    details: { ...details },
    changedLoadingFlags: changedLoadingFlags(span.initialFlags, details),
    startedAt: span.startedAt,
  };
}

function getRegistry(): LoadingTraceRegistry {
  window.__DT_LOADING_TRACE__ ??= { active: {}, history: [] };
  return window.__DT_LOADING_TRACE__;
}

function currentRoute(): string {
  if (typeof window === 'undefined') return 'unknown';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function elapsedMs(span: SpanState): number {
  return Math.max(0, Date.now() - span.startedAtMs);
}

function normalizeThresholds(thresholds: readonly number[]): number[] {
  return [...thresholds]
    .filter(ms => Number.isFinite(ms) && ms > 0)
    .sort((a, b) => a - b);
}

function booleanFlags(
  details: DevLoadingTraceDetails
): Record<string, boolean> {
  return Object.fromEntries(
    Object.entries(details).filter(
      (entry): entry is [string, boolean] => typeof entry[1] === 'boolean'
    )
  );
}

function changedLoadingFlags(
  initialFlags: Record<string, boolean>,
  details: DevLoadingTraceDetails
): Record<string, { from: boolean; to: boolean }> {
  const current = booleanFlags(details);
  const keys = new Set([...Object.keys(initialFlags), ...Object.keys(current)]);
  const changed: Record<string, { from: boolean; to: boolean }> = {};

  for (const key of keys) {
    const from = initialFlags[key] ?? false;
    const to = current[key] ?? false;
    if (from !== to) changed[key] = { from, to };
  }

  return changed;
}

function readTraceDetails(
  details: DevLoadingTraceDetailsSource
): DevLoadingTraceDetails {
  try {
    return typeof details === 'function' ? details() : details;
  } catch (error) {
    return {
      traceDetailsError:
        error instanceof Error ? error.message : 'Failed to read details',
    };
  }
}

function errorTraceDetails(error: unknown): DevLoadingTraceDetails {
  const status =
    error && typeof error === 'object'
      ? (error as { status?: unknown }).status
      : undefined;

  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
      errorStatus:
        typeof status === 'number' || typeof status === 'string'
          ? status
          : null,
    };
  }

  return {
    errorName: typeof error,
    errorMessage: String(error),
    errorStatus:
      typeof status === 'number' || typeof status === 'string' ? status : null,
  };
}
